import * as Cookies from "js-cookie";
import Player from '@vimeo/player';
import { ModalManager, ModalPriority } from "./modal_manager";

function buildCookieName(adId) {
  return `ad_modal_suppression_${adId}`;
}

// 広告モーダル表示抑制有効期限: `次回から表示しない` をチェックした場合は365日間、しなかった場合は7日間
const PERMANENT_SUPPRESSION_DAYS = 365;  // Chromiumの保存上限(400日)を超えない範囲での実質的な永久値
const TEMPORARY_SUPPRESSION_DAYS = 7;    // 広告モーダル仕様で定めた暫定日数

function calculateSuppressionExpires(isPermanent) {
  return isPermanent ? PERMANENT_SUPPRESSION_DAYS : TEMPORARY_SUPPRESSION_DAYS;
}

function bakeAdModalSuppressionCookie(adId, expires) {
  const cookieName = buildCookieName(adId);
  const value = (expires === PERMANENT_SUPPRESSION_DAYS) ? "never" : "later";

  Cookies.set(cookieName, value, { expires, sameSite: "Lax" });
}

/**
 * Disable the Escape key to prevent immediate dismissal of the advertisement modal
 * <dialog> `closedBy` attribute handles this for browsers that support it (Chrome 134+, Firefox 141+)
 * For other browsers (Safari, etc.), we still need to prevent the default behavior of the Escape key
 *
 * @param {HTMLDialogElement} modal
 */
function preventEscapeToCloseModal(modal) {
  const closedBySupported = 'closedBy' in HTMLDialogElement.prototype;
  if (closedBySupported) return;

  modal.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') event.preventDefault();
  }, { capture: true });
}

function attachModalHandlers(modal) {
  // Escapeキーでのモーダル閉鎖を防止
  preventEscapeToCloseModal(modal);

  // ✗ボタンクリックで閉じる
  const closeButton = modal.querySelector(".ad-modal-close");
  closeButton?.addEventListener("click", () => modal.close());

  // close時にcookieに書き込む
  const doNotShowAgainCheckbox = modal.querySelector(".ad-modal-do-not-show-again");
  modal.addEventListener("close", () => {
    const adId = modal.dataset.advertisementId;
    const isPermanent = doNotShowAgainCheckbox?.checked ?? false;
    const expires = calculateSuppressionExpires(isPermanent);

    bakeAdModalSuppressionCookie(adId, expires);
  });
}

/**
 * 広告モーダル用 DialogAdapter。showModal() 直前に ad 動画の再生を試みる。
 */
class AdModalDialogAdapter {
  constructor(element, priority, player, { onClose } = {}) {
    this.element = element;
    this.priority = priority;
    this.player = player;
    this.onClose = onClose;
  }

  open() {
    if (this.player) {
      this.player.play().catch(error => {
        switch (error.name) {
          case 'NotAllowedError':
            // [iOS] auto play failed due to low power mode, etc.
            this.player.pause().then(() => this.player.unload());
            break;
          default:
            break;
        }
      });
    }
    return new Promise((resolve) => {
      this.element.addEventListener("close", () => {
        this.onClose?.(); // openAdModal の resolve 用
        resolve();        // ModalManager の dispatch 用
      }, { once: true });
      this.element.showModal();
    });
  }
}

document.addEventListener("DOMContentLoaded", () => {
  const modal = document.querySelector(".ad-modal");
  if (!modal) return;

  const adId = modal.dataset.advertisementId;
  if (!adId) return;

  attachModalHandlers(modal);

  function initialize() {
    const iframe = modal.querySelector('iframe.ad-modal-video[id^="ad-video-"]');
    if (!iframe) return Promise.resolve(null);

    const player = new Player(iframe);
    return player.ready()
      // mute (auto play policy based)
      .then(() => player.setMuted(true))
      .then(() => player)
      .catch(() => null);
  }

  /**
   * 広告モーダルを表示し、閉じられたら resolve する Promise を返す。
   *
   * @returns {Promise<void>}
   */
  function openAdModal() {
    return new Promise((resolve) => {
      const enqueueAdModal = (player) => {
        const adapter = new AdModalDialogAdapter(
          modal,
          ModalPriority.ADVERTISEMENT,
          player,
          { onClose: () => resolve() }
        );
        ModalManager.enqueue(adapter);
      };

      initialize().then(enqueueAdModal);
    });
  }

  /**
   * 本編再生を一時停止し、広告モーダルを表示する。閉じられたら本編再生を再開する。
   * vimeo.js の本編再生インターセプト時に adModal:requestIntercept イベントのハンドラとして呼ばれる。
   *
   * @param {Object} mainPlayer - Vimeo Player インスタンス（pause, unload, play を持つ）
   * @returns {Promise<void>}
   */
  async function interceptWithAdModal(mainPlayer) {
    mainPlayer.intercepted = true;
    // On failure: clear intercepted, then rethrow so the caller (adModal:requestIntercept handler) can log
    try {
      await mainPlayer.pause();   // 再生を停止
      await mainPlayer.unload();  // 再生前の状態(サムネイル表示)に戻す
      await openAdModal();        // 広告モーダルを表示
      await mainPlayer.play();    // 広告モーダル表示が終わったら本編再生を再開
    } catch (e) {
      mainPlayer.intercepted = false;
      throw e;
    }
  }

  /**
   * Handles custom event `adModal:requestIntercept` (dispatched from vimeo.js `requestAdModalIntercept`)
   *
   * `event.detail` must include:
   * - `player` (required) — Vimeo Player to intercept
   * - `resolve` (required) — this handler must invoke it exactly once on every path (e.g. in `finally`),
   *   including success, failure, or early return
   */
  document.addEventListener("adModal:requestIntercept", async (event) => {
    const { player, resolve } = event.detail;
    try {
      // 有効な表示抑制cookieがあれば広告モーダルを表示しない
      const suppressionCookie = Cookies.get(buildCookieName(adId));
      if (suppressionCookie) return;

      await interceptWithAdModal(player);
    } catch (e) {
      // e.g. Vimeo Player API rejections, autoplay policy (NotAllowedError), network errors
      console.error('[ad_modal] intercept failed', e);
    } finally {
      resolve();
    }
  });
});
